import { Component } from '@theme/component';

/**
 * Scentreat moods switcher.
 * Desktop: clicking a room shows that room's slide (photo + recipe card).
 * Phone/tablet: the stage is a horizontal scroll-snap track — swiping changes the room,
 * tapping a room scrolls to its slide.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} tabs
 * @property {HTMLElement[]} slides
 * @property {HTMLElement} stage
 */

/** @extends {Component<Refs>} */
class StMoodsTabsComponent extends Component {
  requiredRefs = ['tabs', 'slides', 'stage'];

  #current = 0;
  #ignoreScroll = false;
  #timer = 0;

  connectedCallback() {
    super.connectedCallback();
    this.refs.stage.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.stage.removeEventListener('scroll', this.#onScroll);
  }

  get #isSlider() {
    const stage = this.refs.stage;
    return stage.scrollWidth > stage.clientWidth + 1;
  }

  /**
   * @param {{ index: number }} data
   */
  select(data) {
    const index = Number(data.index);
    this.#apply(index);
    if (!this.#isSlider) return;
    const slide = this.refs.slides[index];
    if (!slide) return;
    this.#ignoreScroll = true;
    this.refs.stage.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => { this.#ignoreScroll = false; }, 700);
  }

  #apply(index) {
    this.#current = index;
    this.refs.tabs.forEach((tab, i) => tab.setAttribute('aria-selected', String(i === index)));
    this.refs.slides.forEach((slide, i) => { slide.hidden = i !== index; });
  }

  #onScroll = () => {
    if (this.#ignoreScroll || !this.#isSlider) return;
    const first = this.refs.slides[0];
    if (!first) return;
    const step = first.offsetWidth + 16;
    const index = Math.max(0, Math.min(Math.round(this.refs.stage.scrollLeft / step), this.refs.slides.length - 1));
    if (index !== this.#current) this.#apply(index);
  };
}

if (!customElements.get('st-moods-tabs-component')) {
  customElements.define('st-moods-tabs-component', StMoodsTabsComponent);
}
